import tepService from '@/service/tep-service';
import { getCookie } from '@/utils/cookies';
import { reactive } from 'vue';

const state = reactive({
    department: null,
    tepTreeList: [],
    tepTreeLoadedItems: [],
    tepTreeContextMenu: {},
    tepTreeCreateItemsMenu: {},
    hasAllChildren: [],
    rootParentId: null,
    allParentsId: [],
})

const setDepartment = () => {
    state.department = +getCookie('dep');
}

// region tree data
const getAllChildren = (parentKod) => {
    const childrenKods = [];
    const children = state.tepTreeList.filter(item => item.kod_r === parentKod);
    for (const child of children) {
        childrenKods.push(child.kod);
        const children = getAllChildren(child.kod);
        childrenKods.push(...children);
    }
    return Array.from(new Set(childrenKods));
}

const loadTepTreeData = async (parentId, search = false) => {
    let args = {
        id: parentId,
        direction: 1,
        tep_el: 2,
    }
    if(search) {
        args = {
            ...args,
            direction: 2,
            loadedItems: state.tepTreeLoadedItems
        }
    }
    try {
        let result = await tepService.getTepTreeData(args);
        if(search) {
            state.tepTreeLoadedItems = result.loadedItems;
            result = result.result;
        }
        state.tepTreeList.push(...result);
        state.rootParentId = findRootParent(parentId, state.tepTreeList);
        state.allParentsId = findAllParents(parentId, state.tepTreeList);
        return true
    } catch (e) {
        console.error('Ошибка в loadTepTreeData:', e);
        throw e;
    }
}

const findRootParent = (kod, data) => {
    const map = new Map(data.map(item => [item.kod, item]));
    let current = map.get(kod);

    while (current?.kod_r != null) {
        current = map.get(current.kod_r);
    }
    return current?.kod;
}

const findAllParents = (kod, data) => {
    const map = new Map(data.map(item => [item.kod, item]));
    const parents = [];
    let current = map.get(kod);

    while (current && current.kod_r != null) {
        const parent = map.get(current.kod_r);
        if (!parent) break;
        parents.push(parent.kod);
        current = parent;
    }
    return parents;
}

const loadTepTreeItemAllChildren = async (parentKod) => {
    if (!state.hasAllChildren.includes(parentKod)) {
        const result = await tepService.getAllTreeItemChildren(state.department, parentKod, state.tepTreeLoadedItems);
        const items = result.result;
        state.tepTreeList.push(...items);
        state.tepTreeLoadedItems = result.loadedItems;
        state.hasAllChildren.push(...[
            parentKod,
            ...state.tepTreeList.filter(el => el.kod_r === parentKod).map(el => el.kod),
            ...items.map(el => el.kod)
        ])
    }
}

const clearTreeData = () => {
    state.tepTreeList = [];
    state.tepTreeLoadedItems = [];
    state.hasAllChildren = [];
}

const createTreeItem = async (itemData, kod_r) => {
    let newItemKod = null;
    const result = await tepService.setNewTreeItem(itemData, kod_r);
    if (result.result && result.data) {
        const parentItemInd = state.tepTreeList.findIndex(el => el.kod === kod_r);
        // вторая часть для тех карточек, обработку которых пока не сделала
        newItemKod = result.data.po_kod_ob || state.tepTreeList[state.tepTreeList.length - 1].kod + 1000;
        state.tepTreeList.push({
            kod_r,
            kod: newItemKod,
            name: itemData.name,
            kod_vid: itemData.kod_vid,
            name2: `[${newItemKod}] ${itemData.name}`,
            has_items: false,
            kod_vid_r: parentItemInd !== -1 ? state.tepTreeList[parentItemInd].kod_vid : 0,
            selected: true,
            enabled: true
        });
        state.tepTreeLoadedItems.push(newItemKod);
        if (parentItemInd !== -1) {
            state.tepTreeList[parentItemInd].isExpanded = true;
        }
    }
    return [result, newItemKod];
}

const moveItem = async (kod, kod_r) => {
    const result = await tepService.setTreeItemKodr(kod, kod_r);
    if (result) {
        const ind = state.tepTreeList.findIndex(item => item.kod === kod);
        if (ind !== -1) state.tepTreeList[ind].kod_r = kod_r;
        const parentItemInd = state.tepTreeList.findIndex(item => item.kod === kod_r);
        if (parentItemInd !== -1) state.tepTreeList[parentItemInd].isExpanded = true;
    }
}

const deleteTreeItem = async (kod, kod_r) => {
    const result = await tepService.setTreeItemDeleted(kod);
    if (result) {
        const children = getAllChildren(kod);
        state.tepTreeList = state.tepTreeList
            .filter(item => !children.includes(item.kod) && item.kod !== kod);
        const parentItemInd = state.tepTreeList.findIndex(el => el.kod === kod_r);
        if (parentItemInd !== -1) {
            state.tepTreeList[parentItemInd].has_items = state.tepTreeList
                .filter(el => el.kod_r === kod_r).length !== 0;
        }
    }
}

const updateTreeItem = async (itemData) => {
    const result = await tepService.setTreeItemData(itemData);
    if (result.result) {
        const itemInd = state.tepTreeList.findIndex(el => el.kod === itemData.kod);
        state.tepTreeList[itemInd].name = itemData.name;
        state.tepTreeList[itemInd].name2 = `[${itemData.kod}] ${itemData.name}`;
    }
    return result;
}
// endregion

// region context menu
const loadContextMenuItems = async (kod_vid) => {
    if (!state.tepTreeContextMenu[`kod_vid_${kod_vid}`] || state.tepTreeContextMenu[`kod_vid_${kod_vid}`].length === 0) {
        state.tepTreeContextMenu[`kod_vid_${kod_vid}`] = await tepService.getMenuItemsVid(kod_vid);
    }
    return state.tepTreeContextMenu[`kod_vid_${kod_vid}`]
}

const loadCreateItemsMenu = async (kod_vid) => {
    if (!state.tepTreeCreateItemsMenu[`kod_vid_${kod_vid}`] || state.tepTreeCreateItemsMenu[`kod_vid_${kod_vid}`].length === 0) {
        state.tepTreeCreateItemsMenu[`kod_vid_${kod_vid}`] = await tepService.getCreateItemsMenuVid(kod_vid);
    }
    return state.tepTreeCreateItemsMenu[`kod_vid_${kod_vid}`]
}

const sortMenu = (menu) => {
    const orderString = menu[0].menu_items;
    const orderList = orderString.split(',').map(el => Number(el));
    const orderedMenu = [];
    for (let menu_id of orderList) {
        const menuItem = menu.find(el => el.menu_id === menu_id);
        orderedMenu.push(menuItem);
    }
    return orderedMenu;
}

const setMenuItemChildren = (menu, menuId, createItemsList) => {
    const menuItem = menu.find(el => el.menu_id === menuId);
    const menuInd = menu.indexOf(menuItem ? menuItem : null)
    if (menuInd !== -1) {
        if (createItemsList.length > 0) {
            menu[menuInd].items = createItemsList;
        } else {
            menu.splice(menuInd, 1);
        }
    }
}

const loadAllContextMenuItems = async () => {
    state.tepTreeContextMenu = await tepService.getAllMenuItemsVid();
    state.tepTreeCreateItemsMenu = await tepService.getAllCreateItemsMenuVid();

    for (const vidMenu of Object.keys(state.tepTreeContextMenu)) {
        const menu = state.tepTreeContextMenu[vidMenu];
        const createChildrenItemsMenu = state.tepTreeCreateItemsMenu[vidMenu];

        setMenuItemChildren(menu, 3, createChildrenItemsMenu);
        state.tepTreeContextMenu[vidMenu] = sortMenu(menu);
    }
}

const getMenu = (kod_vid, kod_vid_r) => {
    const menu = [...state.tepTreeContextMenu[`kod_vid_${kod_vid}`]];
    if(kod_vid_r === null) kod_vid_r = 0;
    try {
        const createItemsMenu = state.tepTreeCreateItemsMenu[`kod_vid_${kod_vid_r}`];
        setMenuItemChildren(menu, 2, createItemsMenu);
    } catch (err) {
        console.error('$TEP_INFO$ setCurrentLevelMenu: ', err);
    }
    return menu;
}
// endregion

export default {
    state: state,
    setDepartment: setDepartment,
    getAllChildren: getAllChildren,
    loadTepTreeData: loadTepTreeData,
    loadTepTreeItemAllChildren: loadTepTreeItemAllChildren,
    clearTreeData: clearTreeData,
    createTreeItem: createTreeItem,
    moveItem: moveItem,
    deleteTreeItem: deleteTreeItem,
    updateTreeItem: updateTreeItem,
    loadContextMenuItems: loadContextMenuItems,
    loadCreateItemsMenu: loadCreateItemsMenu,
    loadAllContextMenuItems: loadAllContextMenuItems,
    getMenu: getMenu
}
